import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthProvider';
import { listAttempts, type AttemptDoc } from './attempts';
import { listProgress, type CloudProgress } from './userProgress';
import { listLocalQuizStates } from './quizStorage';

export type CompletionStatus = 'done' | 'in_progress' | 'none';

export interface BestScore {
  correct: number;
  total: number;
  attemptId?: string;
}

export interface SectionStats {
  attempts: number;
  inProgress: boolean;
  best: BestScore | null;
}

interface UserCompletionValue {
  status: (courseSlug: string, sectionSlug: string) => CompletionStatus;
  bestScore: (courseSlug: string, sectionSlug: string) => BestScore | null;
  attemptCount: (courseSlug: string, sectionSlug: string) => number;
  /** Done count for a level. Param is the course-slug prefix (e.g. 'n1-mondai-'). */
  levelStats: (levelPrefix: string) => { done: number; inProgress: number };
  /** Aggregate status across all mondai for a single exam date. */
  examStatus: (levelPrefix: string, examSlug: string) => { doneMondai: number; inProgressMondai: number };
  isLoading: boolean;
  refresh: () => void;
}

const UserCompletionContext = createContext<UserCompletionValue | null>(null);

function keyOf(courseSlug: string, sectionSlug: string): string {
  return `${courseSlug}__${sectionSlug}`;
}

interface InternalData {
  attemptsByKey: Map<string, AttemptDoc[]>;       // all attempts grouped by key
  progressByKey: Map<string, CloudProgress>;      // in-progress (cloud) by key
  guestKeys: Map<string, { done: boolean; in_progress: boolean }>; // localStorage-derived
}

const EMPTY_DATA: InternalData = {
  attemptsByKey: new Map(),
  progressByKey: new Map(),
  guestKeys: new Map(),
};

export function UserCompletionProvider({ children }: { children: ReactNode }) {
  const { user, isReady } = useAuth();
  const uid = user?.uid ?? null;
  const [data, setData] = useState<InternalData>(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const inFlightRef = useRef(0);

  const computeGuest = useCallback((): Map<string, { done: boolean; in_progress: boolean }> => {
    const out = new Map<string, { done: boolean; in_progress: boolean }>();
    const entries = listLocalQuizStates();
    for (const entry of entries) {
      const k = keyOf(entry.courseSlug, entry.sectionSlug);
      const finished = entry.state.status === 'finished';
      const hasPartial =
        Object.keys(entry.state.picks).length > 0 || entry.state.currentTime > 5;
      if (finished) {
        out.set(k, { done: true, in_progress: false });
      } else if (hasPartial) {
        out.set(k, { done: false, in_progress: true });
      }
    }
    return out;
  }, []);

  useEffect(() => {
    if (!isReady) return;
    if (!uid) {
      // guest mode: read localStorage only
      setData({
        attemptsByKey: new Map(),
        progressByKey: new Map(),
        guestKeys: computeGuest(),
      });
      return;
    }
    let cancelled = false;
    const reqId = ++inFlightRef.current;
    setIsLoading(true);
    Promise.all([listAttempts(uid), listProgress(uid)])
      .then(([attempts, progress]) => {
        if (cancelled || reqId !== inFlightRef.current) return;
        const attemptsByKey = new Map<string, AttemptDoc[]>();
        for (const a of attempts) {
          const k = keyOf(a.courseSlug, a.sectionSlug);
          const list = attemptsByKey.get(k);
          if (list) list.push(a);
          else attemptsByKey.set(k, [a]);
        }
        const progressByKey = new Map<string, CloudProgress>();
        for (const p of progress) {
          progressByKey.set(keyOf(p.courseSlug, p.sectionSlug), p);
        }
        setData({ attemptsByKey, progressByKey, guestKeys: new Map() });
      })
      .catch(() => {
        if (cancelled || reqId !== inFlightRef.current) return;
        setData(EMPTY_DATA);
      })
      .finally(() => {
        if (!cancelled && reqId === inFlightRef.current) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // refreshTick included to trigger re-fetch on demand
  }, [uid, isReady, refreshTick, computeGuest]);

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const value: UserCompletionValue = useMemo(
    () => ({
      isLoading,
      refresh,
      status: (courseSlug, sectionSlug) => {
        const k = keyOf(courseSlug, sectionSlug);
        if (!uid) {
          const g = data.guestKeys.get(k);
          if (!g) return 'none';
          if (g.done) return 'done';
          if (g.in_progress) return 'in_progress';
          return 'none';
        }
        if ((data.attemptsByKey.get(k)?.length ?? 0) > 0) return 'done';
        if (data.progressByKey.has(k)) return 'in_progress';
        return 'none';
      },
      bestScore: (courseSlug, sectionSlug) => {
        const k = keyOf(courseSlug, sectionSlug);
        const list = data.attemptsByKey.get(k);
        if (!list || list.length === 0) return null;
        let best: AttemptDoc = list[0];
        for (const a of list) {
          if (
            a.correctCount > best.correctCount ||
            (a.correctCount === best.correctCount && a.submittedAtMs > best.submittedAtMs)
          ) {
            best = a;
          }
        }
        return { correct: best.correctCount, total: best.totalQuestions, attemptId: best.id };
      },
      attemptCount: (courseSlug, sectionSlug) => {
        const k = keyOf(courseSlug, sectionSlug);
        return data.attemptsByKey.get(k)?.length ?? 0;
      },
      examStatus: (levelPrefix, examSlug) => {
        const doneCourses = new Set<string>();
        const inProgressCourses = new Set<string>();
        if (!uid) {
          for (const [k, g] of data.guestKeys.entries()) {
            if (!k.startsWith(levelPrefix)) continue;
            if (!k.endsWith(`__${examSlug}`)) continue;
            const courseSlug = k.split('__')[0];
            if (g.done) doneCourses.add(courseSlug);
            else if (g.in_progress) inProgressCourses.add(courseSlug);
          }
        } else {
          for (const k of data.attemptsByKey.keys()) {
            if (!k.startsWith(levelPrefix)) continue;
            if (!k.endsWith(`__${examSlug}`)) continue;
            doneCourses.add(k.split('__')[0]);
          }
          for (const k of data.progressByKey.keys()) {
            if (!k.startsWith(levelPrefix)) continue;
            if (!k.endsWith(`__${examSlug}`)) continue;
            const courseSlug = k.split('__')[0];
            if (!doneCourses.has(courseSlug)) inProgressCourses.add(courseSlug);
          }
        }
        return { doneMondai: doneCourses.size, inProgressMondai: inProgressCourses.size };
      },
      levelStats: (levelPrefix) => {
        let done = 0;
        let inProgress = 0;
        const counted = new Set<string>();
        if (!uid) {
          for (const [k, g] of data.guestKeys.entries()) {
            if (!k.startsWith(levelPrefix)) continue;
            // De-dup by (sectionSlug) so 1 đề across multiple mondai chỉ tính 1 lần done
            const sectionSlug = k.split('__')[1];
            const dedup = `sec:${sectionSlug}`;
            if (counted.has(dedup)) continue;
            counted.add(dedup);
            if (g.done) done++;
            else if (g.in_progress) inProgress++;
          }
          return { done, inProgress };
        }
        const examIds = new Set<string>();
        const doneExams = new Set<string>();
        const inProgressExams = new Set<string>();
        for (const k of data.attemptsByKey.keys()) {
          if (!k.startsWith(levelPrefix)) continue;
          const sectionSlug = k.split('__')[1];
          examIds.add(sectionSlug);
          doneExams.add(sectionSlug);
        }
        for (const k of data.progressByKey.keys()) {
          if (!k.startsWith(levelPrefix)) continue;
          const sectionSlug = k.split('__')[1];
          examIds.add(sectionSlug);
          if (!doneExams.has(sectionSlug)) inProgressExams.add(sectionSlug);
        }
        return { done: doneExams.size, inProgress: inProgressExams.size };
      },
    }),
    [data, uid, isLoading, refresh],
  );

  return <UserCompletionContext.Provider value={value}>{children}</UserCompletionContext.Provider>;
}

export function useUserCompletion(): UserCompletionValue {
  const ctx = useContext(UserCompletionContext);
  if (!ctx) {
    // Fallback for cases the provider wasn't mounted (e.g., isolated test renders).
    return {
      status: () => 'none',
      bestScore: () => null,
      attemptCount: () => 0,
      levelStats: () => ({ done: 0, inProgress: 0 }),
      examStatus: () => ({ doneMondai: 0, inProgressMondai: 0 }),
      isLoading: false,
      refresh: () => {},
    };
  }
  return ctx;
}
